import { formatUtc, useAsync } from '../../components/Async'
import { deployment, type WayIn } from '../../lib/api'

/**
 * One way in, opened.
 *
 * It carries the facts the table drops and the two acts that belong to a row: build another like this,
 * and revoke. Both were previously reachable only from a card that listed every token ever issued.
 *
 * What it deliberately does not carry is a download. The kit's bytes are not stored - `install.ps1`
 * has the token written into it in plain text, and ADR-S-029 is explicit that a credential a server
 * keeps so it can be downloaded twice outlives every reason it was issued for. So the pane says so
 * where a download control would otherwise sit, rather than leaving a hole somebody reads as a missing
 * feature and files a ticket about (D-443).
 */
export function WayInInspector({
  row,
  onClose,
  onRevoke,
  onBuildAnother
}: {
  row: WayIn
  onClose: () => void
  onRevoke: (row: WayIn) => void
  onBuildAnother: (row: WayIn) => void
}) {
  const isKit = Boolean(row.deploymentPackageId)

  return (
    <aside className="inspector" aria-label="Selected kit or token" data-testid="way-in-inspector">
      <div className="inspector-head">
        <span className="inspector-eyebrow">
          {isKit ? 'Deployment kit' : 'Enrollment token'} · <span className="mono">{row.tokenPrefix}…</span>
        </span>
        <div className="inspector-title-row">
          <h2 data-testid="inspector-name">{row.name}</h2>
          <button
            type="button"
            className="inspector-close"
            aria-label="Close the inspector"
            onClick={onClose}
            data-testid="inspector-close"
          >
            ×
          </button>
        </div>
      </div>

      <div className="inspector-body">
        <dl className="facts">
          <dt>Kind</dt>
          <dd>{isKit ? 'A .zip, built here' : 'A token on its own, no kit'}</dd>

          <dt>Built</dt>
          <dd className="tabular">{formatUtc(row.createdUtc)}</dd>

          {/* "Machines in" rather than "devices": `uses` counts enrollments that succeeded, which is
              not the same as machines running now. A reimaged machine returns to its own device row
              and costs neither a seat nor a use, and a refused attempt costs nothing. Answering
              "devices currently enrolled from this kit" would need a column fleet.device does not
              have, and this screen is not the reason to add one. */}
          <dt>Machines in</dt>
          <dd className="tabular" data-testid="inspector-uses">
            {row.uses.toLocaleString()} of {row.maximumUses.toLocaleString()} allowed
          </dd>

          <dt>Expires</dt>
          <dd data-testid="inspector-expires">
            {row.state === 'live' ? (
              formatUtc(row.expiresUtc)
            ) : (
              <span className="muted">no longer works — {row.state === 'used-up' ? 'used up' : row.state}</span>
            )}
          </dd>

          <dt>Token</dt>
          <dd className="mono">{row.tokenPrefix}…</dd>
        </dl>

        {/*
          What the kit held, read from the record the server kept when it was built.

          This block is the reason the endpoint behind it stopped being dead code. A kit cannot be
          downloaded twice, so "which policies did the machines installed from this one get" had no
          answer in the console at all - and it is a question asked in June about a kit built in March.
          Omitted entirely for a bare token, which has no kit and no manifest to read (D-448).
        */}
        {isKit && <ManifestBlock deploymentPackageId={row.deploymentPackageId!} />}

        {isKit && (
          <div className="inspector-note">
            <span className="inspector-eyebrow">Downloading it again</span>
            <p>
              Not possible. <code>install.ps1</code> had the token written into it in plain text, and
              only its first eight characters and a hash were kept. The .zip was not stored. Build a new
              kit to hand this team one again.
            </p>
          </div>
        )}
      </div>

      <div className="inspector-foot">
        <button
          type="button"
          className="grow"
          onClick={() => onBuildAnother(row)}
          data-testid="inspector-build-another"
        >
          Build another like this
        </button>
        {/* Only a live row. Revoking anything else withdraws a way in that is already closed, and a
            control that does nothing is worse than an absent one. */}
        {row.state === 'live' && (
          <button type="button" className="danger" onClick={() => onRevoke(row)} data-testid="inspector-revoke">
            Revoke
          </button>
        )}
      </div>
    </aside>
  )
}

/**
 * The stored record of one kit's contents.
 *
 * Its own component so its request is its own: the pane renders for every selected row and only a kit
 * has a manifest, so fetching it in the parent would fire a request for every bare token opened. A
 * failure here is deliberately quiet - the facts above it are already on screen and correct, and a
 * banner over them would suggest the row itself could not be read.
 */
function ManifestBlock({ deploymentPackageId }: { deploymentPackageId: string }) {
  const manifest = useAsync(() => deployment.manifest(deploymentPackageId), [deploymentPackageId])

  if (manifest.error) {
    return (
      <div className="inspector-note" data-testid="manifest-unavailable">
        <span className="inspector-eyebrow">What this kit contained</span>
        <p className="muted">
          The record of what this kit held could not be read just now. Nothing is wrong with the kit or
          the token; this is the console.
        </p>
      </div>
    )
  }

  const held = manifest.value
  if (!held) return null

  return (
    <div className="inspector-note" data-testid="kit-manifest">
      <span className="inspector-eyebrow">What this kit contained</span>

      <dl className="facts manifest-facts">
        <dt>Installer</dt>
        <dd className="mono">{held.clientFileName}</dd>

        <dt>Its hash</dt>
        <dd className="mono truncate-hash">{held.clientHash}</dd>

        <dt>Policies</dt>
        <dd className="tabular" data-testid="manifest-policies">
          {held.policies.length.toLocaleString()} published
        </dd>

        <dt>Signing key</dt>
        <dd className="mono">{held.signingAnchorKeyId ?? <span className="muted">none</span>}</dd>

        <dt>Points at</dt>
        <dd className="mono">{held.collectorUri}</dd>
      </dl>

      <p className="hint">
        This is the manifest the server kept when the kit was built. It answers which policies the
        machines installed from this kit were given.
      </p>
    </div>
  )
}
